requestAnimationFrame(() => {
  themeBase.onDOMContentLoadedTrigger(() => {
    scriptController.executeWhenScriptLoaded('search-form.js', () => {
      if (!customElements.get('predictive-search')) {
        class PredictiveSearch extends SearchForm {
          constructor() {
            super();

            this.cachedResults = {};
            this.form = this.querySelector('form');
            this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
            this.predictiveSearchCategory = this.querySelector('[data-predictive-search-category]');
            this.menuDrawer = this.closest('#menu-drawer');
            this.navigationContainer = this.closest('.menu-drawer__navigation-container');
            this.allPredictiveSearchInstances =
              document.querySelectorAll('predictive-search');
            this.popupModule = this.querySelector('popup-module');
            this.isOpen = false;
            this.requestId = 0;
            this.searchTerm = '';

          }

          connectedCallback() {
            super.connectedCallback();
            if (this.listeners) return;
            this.listeners = new AbortController();
            const options = { signal: this.listeners.signal };
            this.input.form.addEventListener('submit', this.onFormSubmit.bind(this), options);
            this.input.addEventListener('focus', this.onFocus.bind(this), options);
            this.addEventListener('focusout', this.onFocusOut.bind(this), options);
            this.addEventListener('keyup', this.onKeyup.bind(this), options);
            this.addEventListener('keydown', this.onKeydown.bind(this), options);
            this.popupModule?.addEventListener('popupClosed', () => this.close(), options);
            this.predictiveSearchCategory?.addEventListener('change', () => {
              clearTimeout(this.inputTimer);
              this.onInputChange();
              this.onChange();
            }, options);
          }

          disconnectedCallback() {
            super.disconnectedCallback();
            this.listeners?.abort();
            this.listeners = null;
            clearTimeout(this.focusTimer);
            clearTimeout(this.statusTimer);
            this.close();
          }

          cancelSearch() {
            this.requestId += 1;
            this.abortController?.abort();
            clearTimeout(this.requestTimer);
          }

          onInputChange() {
            this.close();
            this.removeAttribute('results');
          }

          getQuery() {
            return this.input.value.trim();
          }

          onChange() {
            if (!this.isConnected) return;
            super.onChange();
            const newSearchTerm = this.getQuery();
            if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
              this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
            }

            this.updateSearchForTerm(this.searchTerm, newSearchTerm);

            this.searchTerm = newSearchTerm;

            if (!this.searchTerm.length) {
              this.close(true);
              return;
            }

            this.getSearchResults(this.searchTerm);
          }

          onFormSubmit(event) {
            if (!this.getQuery().length && this.predictiveSearchCategory && this.predictiveSearchCategory.value !== '') {
              this.input.value = '*';
            }

            if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
          }

          onFormReset(event) {
            super.onFormReset(event);
            if (super.shouldResetForm()) {
              this.searchTerm = '';
              this.close(true);
            }
          }

          onFocus() {
            const currentSearchTerm = this.getQuery();

            if (!currentSearchTerm.length) return;

            if (this.searchTerm !== currentSearchTerm) {
              this.onChange();
            } else if (this.getAttribute('results') === 'true') {
              this.open();
            } else {
              this.getSearchResults(this.searchTerm);
            }
          }

          onFocusOut() {
            if (!this.dataset.closeOnfocus) return;
            clearTimeout(this.focusTimer);
            this.focusTimer = setTimeout(() => {
              if (this.isConnected && !this.contains(document.activeElement)) this.close();
            })
          }

          onKeyup(event) {
            if (!this.getQuery().length) this.close(true);
            event.preventDefault();

            switch (event.code) {
              case 'ArrowUp':
                this.switchOption('up')
                break;
              case 'ArrowDown':
                this.switchOption('down');
                break;
              case 'Enter':
                this.selectOption();
                break;
            }
          }

          onKeydown(event) {
            if (event.key === 'Escape') {
              clearTimeout(this.inputTimer);
              this.close();
              return;
            }
            if (
              event.code === 'ArrowUp' ||
              event.code === 'ArrowDown'
            ) {
              event.preventDefault();
            }
          }

          updateSearchForTerm(previousTerm, newTerm) {
            const searchForTextElement = this.querySelector(
              "[data-predictive-search-search-for-text]"
            );
            const currentButtonText = searchForTextElement?.innerText;
            if (currentButtonText && previousTerm) {
              if (currentButtonText.split(previousTerm).length !== 2) {
                return;
              }
              const newButtonText = currentButtonText.replace(previousTerm, newTerm);
              searchForTextElement.innerText = newButtonText;
            }
          }

          switchOption(direction) {
            if (!this.getAttribute('open')) return;

            const moveUp = direction === 'up';
            const selectedElement = this.querySelector('[aria-selected="true"]');

            const allVisibleElements = Array.from(
              this.querySelectorAll("li, button.predictive-search__item")
            ).filter((element) => element.offsetParent !== null);
            let activeElementIndex = 0;

            if (moveUp && !selectedElement) return;

            let selectedElementIndex = -1;
            let i = 0;

            while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
              if (allVisibleElements[i] === selectedElement) {
                selectedElementIndex = i;
              }
              i++;
            }

            this.statusElement.textContent = "";

            if (!moveUp && selectedElement) {
              activeElementIndex =
                selectedElementIndex === allVisibleElements.length - 1 ?
                0 :
                selectedElementIndex + 1;
            } else if (moveUp) {
              activeElementIndex =
                selectedElementIndex === 0 ?
                allVisibleElements.length - 1 :
                selectedElementIndex - 1;
            }

            if (!allVisibleElements.length || activeElementIndex === selectedElementIndex) return;

            const activeElement = allVisibleElements[activeElementIndex];

            activeElement.setAttribute('aria-selected', true);
            if (selectedElement) selectedElement.setAttribute('aria-selected', false);

            this.input.setAttribute('aria-activedescendant', activeElement.id);
          }

          selectOption() {
            const selectedOption = this.querySelector(
              '[aria-selected="true"] a, button[aria-selected="true"]'
            );

            if (selectedOption) selectedOption.click();
          }

          async getSearchResults(searchTerm) {
            this.cancelSearch();
            if (!this.isConnected) return;
            const requestId = this.requestId;
            const categoryType = this.predictiveSearchCategory?.name || '';
            const categoryValue = this.predictiveSearchCategory?.value || '';
            const queryKey = JSON.stringify([searchTerm, categoryType, categoryValue]);
            const isCurrent = () => this.isConnected && requestId === this.requestId &&
              this.getQuery() === searchTerm &&
              (this.predictiveSearchCategory?.value || '') === categoryValue;
            this.abortController = new AbortController();
            const controller = this.abortController;
            this.categoryType = categoryValue ? categoryType : '';
            this.categoryValue = categoryValue;
            this.setLiveRegionLoadingState();
            this.requestTimer = setTimeout(() => {
              if (isCurrent()) controller.abort();
            }, 15000);
            try {
              let markup = this.cachedResults[queryKey];
              if (markup === undefined) {
                const category = categoryValue ? '&resources[type]=product' : '';
                const response = await fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}${category}&section_id=predictive-search`, { signal: controller.signal });
                if (!response.ok) throw new Error('Search request failed');
                const text = await response.text();
                if (!isCurrent()) return;
                const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
                if (!section) throw new Error('Search response is missing its section');
                markup = section.innerHTML;
                document.querySelectorAll('predictive-search').forEach(instance => {
                  instance.cachedResults[queryKey] = markup;
                  const keys = Object.keys(instance.cachedResults);
                  if (keys.length > 50) delete instance.cachedResults[keys[0]];
                });
              }
              if (isCurrent()) this.renderSearchResults(markup);
            } catch (error) {
              if (!isCurrent()) return;
              this.closeResults();
              this.removeAttribute('results');
              this.setLiveRegionText('Suggestions are unavailable. Press Enter to search.');
            } finally {
              if (requestId === this.requestId) {
                clearTimeout(this.requestTimer);
                this.removeAttribute('loading');
              }
            }
          }

          setLiveRegionLoadingState() {
            this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
            this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

            this.setLiveRegionText(this.loadingText);
            this.setAttribute('loading', true);

            if (this.menuDrawer) {
              this.navigationContainer.scrollTo(0, 0);
              this.menuDrawer.classList.add('menu-drawer--opened-predictive-search');
            }
          }

          setLiveRegionText(statusText) {
            if (!this.statusElement) return;
            clearTimeout(this.statusTimer);
            this.statusElement.setAttribute('aria-hidden', 'false');
            this.statusElement.textContent = statusText;

            this.statusTimer = setTimeout(() => {
              if (!this.isConnected) return;
              this.statusElement.setAttribute('aria-hidden', 'true');
            }, 1000);
          }

          renderSearchResults(resultsMarkup) {
            this.predictiveSearchResults.innerHTML = resultsMarkup;

            if (this.categoryType) {
              const attribute = this.categoryType === 'filter.p.product_type' ? 'data-product-type' : 'data-vendor';
              const productsToRemove = Array.from(this.predictiveSearchResults.querySelectorAll('.predictive-search__list-item'))
                .filter(item => item.getAttribute(attribute) !== this.categoryValue);

              productsToRemove.forEach(element => element.remove());

              const filteredProductsList = this.predictiveSearchResults.querySelectorAll('.predictive-search__list-item');

              if (!filteredProductsList.length) {
                this.predictiveSearchResults.querySelector('.predictive-search__result-group')?.remove();
              }
            }


            this.setAttribute('results', true);
            this.predictiveSearchResults.querySelector('.predictive-search__search-for-button button')?.setAttribute('form', this.form.id);
            this.setLiveRegionResults();
            this.open();
          }

          setLiveRegionResults() {
            this.removeAttribute('loading');
            this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent || '');
          }

          getResultsMaxHeight() {
            this.resultsMaxHeight = window.innerHeight - themeBounds.get(document.querySelector('.section-header')).bottom;
            return this.resultsMaxHeight;
          }

          open() {
            if (this.dataset.bodyClass) {
              document.body.classList.add(this.dataset.bodyClass);
            }

            themeSettings.manuallyIgnoreStickyHeader = true;
            this.setAttribute('open', true);
            this.input.setAttribute('aria-expanded', true);
            this.isOpen = true;
          }

          close(clearSearchTerm = false) {
            clearTimeout(this.inputTimer);
            this.cancelSearch();
            themeSettings.manuallyIgnoreStickyHeader = false;
            this.closeResults(clearSearchTerm);

            if (this.dataset.bodyClass) {
              document.body.classList.remove(this.dataset.bodyClass);
            }

            this.isOpen = false;
          }

          closeResults(clearSearchTerm = false) {
            if (clearSearchTerm) {
              this.input.value = '';
              this.removeAttribute('results');
            }
            const selected = this.querySelector('[aria-selected="true"]');

            if (selected) selected.setAttribute('aria-selected', false);

            this.input.setAttribute('aria-activedescendant', '');
            this.removeAttribute('loading');
            this.removeAttribute('open');
            this.input.setAttribute('aria-expanded', false);
            this.resultsMaxHeight = false
            this.predictiveSearchResults.removeAttribute('style');

            if (this.menuDrawer) {
              this.menuDrawer.classList.remove('menu-drawer--opened-predictive-search');
            }
          }
        }

        customElements.define('predictive-search', PredictiveSearch);
      }
    });
  });
});